import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import getAPI from "../../service/getAPI";
import { Button, Modal } from 'react-bootstrap';
import AddNew from './Store-addnew';
import { format } from 'date-fns';

function Store() {
    const styles = {
        row: {
            marginBottom: "12px",
            fontSize: "14px"
        },
        label: {
            fontWeight: 600,
            color: "#475569"
        }
    };

    const { master } = getAPI();
    const [showViewModal, setShowViewModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [viewData, setViewData] = useState(null);
    const [rawList, setRawList] = useState([]);
    const [loading, setLoading] = useState(false);

    // Search and filter states
    const [searchInput, setSearchInput] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "inactive"

    // Pagination states
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const res = await axios.post(master.storeListPaginated, {
                page: 1,
                limit: 100,
                search: { value: "" }
            });

            if (res.data.flag === 1) {
                const data = res.data.data.data || res.data.data || [];
                setRawList(data);
            } else {
                setRawList([]);
            }
        } catch (err) {
            console.error("Error loading branch list:", err);
            setRawList([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter list based on search and status
    const filteredList = useMemo(() => {
        let result = [...rawList];

        // Status filter
        if (statusFilter === "active") {
            result = result.filter(store => store.is_active === 1 || store.is_active === true);
        } else if (statusFilter === "inactive") {
            result = result.filter(store => store.is_active === 0 || store.is_active === false);
        }

        // Search query
        if (searchInput.trim()) {
            const query = searchInput.toLowerCase().trim();
            result = result.filter(store => {
                const name = (store.store_name || "").toLowerCase();
                const phone = (store.mobile_no || "").toLowerCase();
                const address = (store.address || "").toLowerCase();
                const area = (store.area || "").toLowerCase();
                const city = (store.city || "").toLowerCase();
                const pincode = (store.pincode || "").toLowerCase();
                return name.includes(query) || phone.includes(query) || address.includes(query) || area.includes(query) || city.includes(query) || pincode.includes(query);
            });
        }

        return result;
    }, [rawList, searchInput, statusFilter]);

    // Paginated slice
    const totalCount = filteredList.length;
    const totalPages = Math.ceil(totalCount / limit) || 1;
    const paginatedList = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredList.slice(start, start + limit);
    }, [filteredList, page, limit]);

    // Reset to page 1 on search or filter change
    useEffect(() => {
        setPage(1);
    }, [searchInput, statusFilter, limit]);

    const handleDelete = async (store) => {
        if (store.store_id === 1 || store.user_type === 'admin') {
            alert("The Main Admin branch cannot be deleted.");
            return;
        }

        if (window.confirm(`Are you sure you want to delete branch "${store.store_name}"?`)) {
            try {
                const res = await axios.post(`${master.storeDelete}`, {
                    store_id: store.store_id
                });

                if (res.data.flag === 1) {
                    alert("Branch deleted successfully.");
                    loadData();
                } else {
                    alert(res.data.message || "Failed to delete the branch.");
                }
            } catch (err) {
                console.error("Delete error:", err);
                alert("An error occurred while deleting the branch.");
            }
        }
    };

    const handleModalClose = () => {
        setShowViewModal(false);
        setShowFormModal(false);
        setEditData(null);
        setViewData(null);
        loadData();
    };

    const handleEditClick = (store) => {
        setEditData(store);
        setShowFormModal(true);
    };

    const handleViewClick = (store) => {
        setViewData(store);
        setShowViewModal(true);
    };

    return (
        <main id="main" className="main">
            <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap mb-3">
                <div>
                    <h1>Manage Branches</h1>
                    <nav>
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item"><a href="/admin/dashboard">Home</a></li>
                            <li className="breadcrumb-item active">Branch Management</li>
                        </ol>
                    </nav>
                </div>
            </div>

            <div className="card shadow-sm border-0">
                {/* Header Controls: Search, Filter, Add New */}
                <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                        {/* Search Input */}
                        <div className="input-group" style={{ width: "320px", maxWidth: "100%" }}>
                            <span className="input-group-text bg-light border-end-0">
                                <i className="bi bi-search text-muted"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0 ps-0 bg-light"
                                placeholder="Search by name, mobile, city..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                            {searchInput && (
                                <button className="btn btn-light text-muted border border-start-0" onClick={() => setSearchInput("")}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            )}
                        </div>

                        {/* Status Filter */}
                        <select
                            className="form-select bg-light"
                            style={{ width: "150px" }}
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                    </div>

                    {/* Add New Button */}
                    <div>
                        <Button
                            variant="success"
                            className="shadow-sm fw-bold px-3"
                            onClick={() => {
                                setEditData(null);
                                setShowFormModal(true);
                            }}
                        >
                            <i className="bi bi-plus-circle me-1"></i> Add New Branch
                        </Button>
                    </div>
                </div>

                <div className="card-body pt-3">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: "50px" }}>#</th>
                                    <th>Store Name</th>
                                    <th>Mobile No.</th>
                                    <th>Address</th>
                                    <th className="text-center" style={{ width: "100px" }}>Status</th>
                                    <th className="text-center" style={{ width: "120px" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : paginatedList.length > 0 ? (
                                    paginatedList.map((store, index) => {
                                        const isMainAdmin = store.store_id === 1 || store.user_type === 'admin';
                                        return (
                                            <tr key={store.store_id || index}>
                                                <td className="fw-semibold text-muted">
                                                    {(page - 1) * limit + index + 1}
                                                </td>
                                                <td>
                                                    <span className="fw-bold text-dark">
                                                        {store.store_name}
                                                    </span>
                                                    {isMainAdmin && (
                                                        <span className="badge bg-primary ms-2" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
                                                            MAIN ADMIN
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-nowrap">
                                                    <span className="text-dark fw-semibold">
                                                        {store.mobile_no}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ maxWidth: "420px", fontSize: "13px" }} className="text-truncate" title={[store.address, store.area, store.city, store.state, store.pincode].filter(Boolean).join(', ')}>
                                                        {[store.address, store.area, store.city, store.state, store.pincode].filter(Boolean).join(', ')}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge ${store.is_active === 1 || store.is_active === true ? 'bg-success' : 'bg-secondary'}`}>
                                                        {store.is_active === 1 || store.is_active === true ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-inline-flex align-items-center gap-2">
                                                        <span
                                                            className="actionView cursorP text-primary"
                                                            onClick={() => handleViewClick(store)}
                                                            title="View Details"
                                                        >
                                                            <i className="bi bi-eye fs-5"></i>
                                                        </span>
                                                        <span
                                                            className="actionEdit cursorP text-warning"
                                                            onClick={() => handleEditClick(store)}
                                                            title="Edit Branch"
                                                        >
                                                            <i className="bi bi-pencil-square fs-5"></i>
                                                        </span>
                                                        {!isMainAdmin && (
                                                            <span
                                                                className="actionDelete cursorP text-danger"
                                                                onClick={() => handleDelete(store)}
                                                                title="Delete Branch"
                                                            >
                                                                <i className="bi bi-trash fs-5"></i>
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="text-muted my-3">
                                                <i className="bi bi-shop fs-1 d-block mb-2 text-secondary opacity-50"></i>
                                                <h5>No branches found</h5>
                                                <p className="small text-muted">Try adjusting your search criteria or filter.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination Footer */}
                <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center flex-wrap py-3">
                    <div className="text-muted small mb-2 mb-md-0">
                        {totalCount === 0 ? (
                            <span>No branches to display</span>
                        ) : (
                            <span>
                                Showing <strong className="text-dark">{(page - 1) * limit + 1}</strong> to{" "}
                                <strong className="text-dark">{Math.min(page * limit, totalCount)}</strong> of{" "}
                                <strong className="text-dark">{totalCount}</strong> branches
                            </span>
                        )}
                    </div>
                    <div className="d-flex align-items-center">
                        <div className="d-flex align-items-center me-4">
                            <label htmlFor="pageSize" className="text-muted small me-2 mb-0">Show:</label>
                            <select
                                id="pageSize"
                                className="form-select form-select-sm"
                                style={{ width: "75px" }}
                                value={limit}
                                onChange={(e) => {
                                    setLimit(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        {totalPages > 1 && (
                            <div className="d-flex align-items-center gap-1">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                    disabled={page === 1}
                                >
                                    &laquo; Prev
                                </Button>
                                <select
                                    className="form-select form-select-sm w-auto mx-1"
                                    value={page}
                                    onChange={(e) => setPage(Number(e.target.value))}
                                >
                                    {Array.from({ length: totalPages }, (_, i) => (
                                        <option key={i} value={i + 1}>Page {i + 1}</option>
                                    ))}
                                </select>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={page === totalPages}
                                >
                                    Next &raquo;
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* View Modal */}
            <Modal show={showViewModal} onHide={handleModalClose} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold">
                        <i className="bi bi-shop me-2 text-primary"></i>Branch Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {viewData && (
                        <>
                            <div className="row" style={styles.row}>
                                <div className="col-4" style={styles.label}>Branch Name:</div>
                                <div className="col-8 fw-bold">
                                    {viewData.store_name || "N/A"}
                                    {(viewData.store_id === 1 || viewData.user_type === 'admin') && (
                                        <span className="badge bg-primary ms-2" style={{ fontSize: "10px" }}>MAIN ADMIN</span>
                                    )}
                                </div>
                            </div>

                            <div className="row" style={styles.row}>
                                <div className="col-4" style={styles.label}>Mobile No:</div>
                                <div className="col-8">{viewData.mobile_no || "N/A"}</div>
                            </div>

                            <div className="row" style={styles.row}>
                                <div className="col-4" style={styles.label}>Address:</div>
                                <div className="col-8">
                                    {[viewData.address, viewData.area, viewData.city, viewData.state, viewData.country, viewData.pincode].filter(Boolean).join(', ') || "N/A"}
                                </div>
                            </div>

                            <div className="row" style={styles.row}>
                                <div className="col-4" style={styles.label}>Coordinates:</div>
                                <div className="col-8 text-muted small">
                                    Lat: {viewData.lat || 'N/A'}, Lng: {viewData.lng || 'N/A'}
                                </div>
                            </div>

                            <div className="row" style={styles.row}>
                                <div className="col-4" style={styles.label}>Status:</div>
                                <div className="col-8">
                                    <span className={`badge ${viewData.is_active === 1 || viewData.is_active === true ? 'bg-success' : 'bg-secondary'}`}>
                                        {viewData.is_active === 1 || viewData.is_active === true ? "Active" : "Inactive"}
                                    </span>
                                </div>
                            </div>

                            <div className="row" style={styles.row}>
                                <div className="col-4" style={styles.label}>Created At:</div>
                                <div className="col-8 text-muted">
                                    {viewData.created_at ? format(new Date(viewData.created_at), "dd-MM-yyyy HH:mm") : "N/A"}
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={handleModalClose}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Add/Edit Modal */}
            <Modal show={showFormModal} onHide={handleModalClose} size="lg" centered>
                <AddNew onClose={handleModalClose} editData={editData} storeId={editData?.store_id} />
            </Modal>
        </main>
    );
}

export default Store;
